const TRANSITION_DURATION = 300;

class NavDesktopMenu {
	constructor () {
		const ns = 'nav-desktop-menu';

		this.selectors = {
			panelRoot: `.${ ns }__panel`,
			panelLeft: `.${ ns }__panel-left`,
			panelRight: `.${ ns }__panel-right`,
			leftPanelTrigger: `.${ ns }__nav-item-link.menu-trigger`,
			rightPanelTrigger: `button.${ ns }__top-nav-link`,
			topNavLink: `a.${ ns }__top-nav-link`,
			bottomNavLink: `a.${ ns }__bottom-nav-link`,
		};

		this.elements = {
			panelRoot: document.querySelector(this.selectors.panelRoot),
			leftPanels: document.querySelectorAll(this.selectors.panelLeft),
			rightPanels: document.querySelectorAll(this.selectors.panelRight),
			getPanelLeft: (panel) => document.querySelector(`.${ ns } [data-panel-left="${ panel }"]`),
			getPanelRight: (panel) => document.querySelector(`.${ ns } [data-panel-right="${ panel }"]`),
			leftPanelTriggers: document.querySelectorAll(this.selectors.leftPanelTrigger),
			rightPanelTriggers: document.querySelectorAll(this.selectors.rightPanelTrigger),
			topNavLinks: document.querySelectorAll(this.selectors.topNavLink),
			bottomNavLinks: document.querySelectorAll(this.selectors.bottomNavLink),
		};
	}

	wait (ms) {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	enableBodyScroll () {
		document.body.classList.remove('modal-open');
	}

	disableBodyScroll () {
		document.body.classList.add('modal-open');
	}

	openPanel (panelEl) {
		if (!panelEl) {
			return;
		}

		this.disableBodyScroll();
		panelEl.classList.remove('no-animation');
		panelEl.classList.remove('is-closed');
		panelEl.classList.add('is-opening');

		this.wait(TRANSITION_DURATION).then(() => {
			panelEl.classList.remove('is-closed'); // In case the transition didn't finish
			panelEl.classList.remove('is-opening');
			panelEl.classList.add('is-open');
		});
	}

	closePanel (panelEl) {
		if (!panelEl) {
			return;
		}

		this.enableBodyScroll();
		panelEl.classList.remove('is-open');
		panelEl.classList.add('is-closing');

		this.wait(TRANSITION_DURATION).then(() => {
			panelEl.classList.remove('is-open'); // In case the transition didn't finish
			panelEl.classList.remove('is-closing');
			panelEl.classList.add('is-closed');
		});
	}

	handleLeftPanelMouseLeave (e) {
		const relatedTarget = e.relatedTarget || e.toElement;
		const panel = e.target.dataset.panelLeft;
		const panelLeft = this.elements.getPanelLeft(panel);
		const panelRight = document.querySelector('[data-panel-right].is-open');
		const allRightPanels = document.querySelectorAll('[data-panel-right]');

		if (panelRight?.contains(relatedTarget)) {
			return;
		}

		this.closePanel(panelLeft);
		allRightPanels.forEach(this.closePanel.bind(this));
	}

	handleRightPanelMouseLeave (e) {
		const relatedTarget = e.relatedTarget || e.toElement;
		const panel = e.target.dataset.panelRight;
		const panelRight = this.elements.getPanelRight(panel);
		const panelLeft = document.querySelector('[data-panel-left].is-open');

		if (!panelLeft?.contains(relatedTarget)) {
			this.closePanel(panelLeft);
		}

		this.closePanel(panelRight);
	}

	handleLinkMouseEnter (e) {
		if (e.type === 'mouseenter' || e.key === ' ' || e.keyCode === 32) {
			const panel = e.target.closest('li').dataset.panel;
			const panelLeft = this.elements.getPanelLeft(panel);

			this.openPanel(panelLeft);
		}
	}

	handleLinkMouseLeave (e) {
		const relatedTarget = e.relatedTarget || e.toElement;
		const panel = e.target.closest('li').dataset.panel;
		const panelLeft = this.elements.getPanelLeft(panel);
		const panelRight = document.querySelector('[data-panel-right].is-open');

		if (panelLeft?.contains(relatedTarget) || panelRight?.contains(relatedTarget)) {
			return;
		}

		this.closePanel(panelLeft);
		this.closePanel(panelRight);
	}

	handleSubLinkMouseEnter (e) {
		if (e.type === 'mouseenter' || e.key === ' ') {
			const subPanel = e.target.closest('li').dataset.subPanel;
			const currentlyOpenPanelRight = document.querySelector('[data-panel-right].is-open');
			const panelRight = this.elements.getPanelRight(subPanel);

			this.closePanel(currentlyOpenPanelRight);
			this.openPanel(panelRight);
		}
	}

	handleSubLinkMouseLeave (e) {
		const relatedTarget = e.relatedTarget || e.toElement;
		const subPanel = e.target.closest('li').dataset.subPanel;
		const panelRoot = this.elements.panelRoot;
		const panelRight = this.elements.getPanelRight(subPanel);

		if (panelRoot?.contains(relatedTarget) && !relatedTarget?.closest('button')) {
			return;
		}

		this.closePanel(panelRight);
	}

	handleNavLinkMouseEnter (e) {
		const allRightPanels = document.querySelectorAll('[data-panel-right]');

		allRightPanels.forEach((panel) => {
			const includedClasses = ['is-open', 'is-opening', 'is-closing'];
			if (panel.classList.contains(...includedClasses)) {
				this.closePanel(panel);
			}
		});
	}

	handleEscapeKey (e) {
		const key = e.key || e.keyCode;

		if (key === 'Escape' || key === 'Esc' || key === 27) {
			const panelLeft = document.querySelector('[data-panel-left].is-open');
			const panelRight = document.querySelector('[data-panel-right].is-open');

			this.closePanel(panelLeft);
			this.closePanel(panelRight);
		}
	}

	bindEvents () {
		this.elements.leftPanelTriggers.forEach(leftPanelTrigger => {
			leftPanelTrigger.addEventListener('keydown', this.handleLinkMouseEnter.bind(this));
			leftPanelTrigger.addEventListener('click', this.handleLinkMouseEnter.bind(this));
			leftPanelTrigger.addEventListener('mouseenter', this.handleLinkMouseEnter.bind(this));
			leftPanelTrigger.closest('li').addEventListener('mouseleave', this.handleLinkMouseLeave.bind(this));
		});

		this.elements.rightPanelTriggers.forEach(rightPanelTrigger => {
			rightPanelTrigger.addEventListener('keydown', this.handleSubLinkMouseEnter.bind(this));
			rightPanelTrigger.addEventListener('click', this.handleSubLinkMouseEnter.bind(this));
			rightPanelTrigger.addEventListener('mouseenter', this.handleSubLinkMouseEnter.bind(this));
			rightPanelTrigger.addEventListener('mouseleave', this.handleSubLinkMouseLeave.bind(this));
		});

		this.elements.topNavLinks.forEach(topNavLink => {
			topNavLink.addEventListener('mouseenter', this.handleNavLinkMouseEnter.bind(this));
		});

		this.elements.bottomNavLinks.forEach(bottomNavLink => {
			bottomNavLink.addEventListener('mouseenter', this.handleNavLinkMouseEnter.bind(this));
		});

		this.elements.leftPanels.forEach(panel => {
			panel.addEventListener('mouseleave', this.handleLeftPanelMouseLeave.bind(this));
		});

		this.elements.rightPanels.forEach(panel => {
			panel.addEventListener('mouseleave', this.handleRightPanelMouseLeave.bind(this));
		});

		document.addEventListener('keydown', this.handleEscapeKey.bind(this));
	};

	init () {
		this.bindEvents();
	}
}

const navDesktopMenu = new NavDesktopMenu();

navDesktopMenu.init();
